/**
 * useDispatchTasks Hook
 *
 * Dispatch Console 页面的数据管理 Hook
 * 用于替换 mockContainerData 静态数据
 *
 * 功能：
 * - 从 API 获取任务列表
 * - 从 API 获取司机/货主/仓库下拉选项
 * - 提供任务更新功能
 * - 数据格式转换（后端 -> 前端）
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { message } from 'antd';
import { ContainerTask, DriverData, LocationType, RouteNode } from '../types/container';
import type { ContainerHoldDetail } from '../types/scraper';
import {
  taskAPI,
  BackendTask,
  TaskFilters,
  mapBackendStatusToFrontend,
  mapBackendTaskTypeToFrontend,
  mapFrontendStatusToBackend,
  mapFrontendTaskTypeToBackend
} from '../services/taskApi';
import { shipperAPI, warehouseClientAPI } from '../services/api';
import { driverApi } from '../services/driverApi';
import { getAllPorts } from '../services/portApi';
// Phase 5: Import real-time hook for WebSocket integration
import { useRealtimeTasks } from './useRealtimeTasks';
// B3 Fix Plate — this hook owns the flow instance and re-exports startFixPlate
// so the task rows can drive it (see UseDispatchTasksReturn).
import { useFixPlateFlow } from './useFixPlateFlow';
// Gate Fee — same arrangement as Fix Plate: one flow instance owned here,
// re-exported so the rows can drive it without each mounting a socket listener.
import { useGateFeeFlow } from './useGateFeeFlow';
import { socketService } from '../services/socket';

// Port data interface
interface PortOption {
  id: string;
  name: string;
  shortName?: string;
  code?: string;
}

// Warehouse dropdown option — filtering happens by id, so the row identity has
// to travel with the label.
interface WarehouseOption {
  id: string;
  name: string;
  shortName?: string;
}

// API response item types for dropdown data
interface DriverItem {
  id: number;
  full_name?: string;
  name?: string;
  avatar_url?: string;
  status?: string;
  current_location?: string;
  rating?: number;
  phone?: string;
  email?: string;
  company?: string;
  truck_id?: number;
  truck_plate?: string;
}
// Shipper getAll returns camelCase aliases from backend
interface ShipperItem { companyName?: string; shortName?: string; name?: string; short_name?: string }
// GET /warehouse-clients aliases every column to camelCase, same as /ports —
// reading warehouse_name/short_name here left the dropdown permanently empty.
interface WarehouseItem { id: number | string; warehouseName?: string; shortName?: string }
// GET /ports returns camelCase (see types/port.ts) — reading port_name/port_code
// here silently produced an all-empty list and an empty Port dropdown.
interface PortItem { id: number | string; portName?: string; shortName?: string; portCode?: string }

/**
 * 扣柜原因那一小包（服务端 dispatchTask.model 的 `hold_info`）。
 * 这是从柜子监控行上转述来的，所以连「什么时候问到的」「还在跟吗」一起带回来 ——
 * 少了这两样，一条停掉半个月的监控会拿旧话冒充现在的事实。
 */
interface BackendHoldInfo {
  details: ContainerHoldDetail[] | null;
  tags: string[] | null;
  terminal_codes: string | null;
  checked_at: string | null;
  monitor_status: string | null;
}

// Stable empty object reference to avoid infinite loops
const EMPTY_FILTERS: TaskFilters = {};

// ==================== Transform Functions ====================

/**
 * 将后端 Task 数据转换为前端 ContainerTask 格式
 */
export const transformBackendToFrontend = (task: BackendTask): ContainerTask => {
  // 柜子为什么被扣着。服务端从柜子监控那行一次打包回来（dispatchTask.model 的
  // hold_info），On Hold 那一行在状态底下照原话显示 —— 任务表自己没有这个答案。
  const holdInfo = (task as BackendTask & { hold_info?: BackendHoldInfo | null }).hold_info || null;
  return {
    id: String(task.id),
    taskId: task.task_id || `TASK-${String(task.id).padStart(6, '0')}`,
    containerNumber: task.container_number || '',
    shippingLine: task.shipping_line || '',
    shippingLineCode: task.shipping_line_code || '',
    taskType: mapBackendTaskTypeToFrontend(task.task_type) as ContainerTask['taskType'],
    taskSubtype: (task.task_subtype || 'drop') as ContainerTask['taskSubtype'],
    status: mapBackendStatusToFrontend(task.status) as ContainerTask['status'],
    // B3 Fix Plate — server-derived, see PLATE_FIX_APPOINTMENT_ID_SQL
    plateFixAppointmentId:
      (task as BackendTask & { plate_fix_appointment_id?: number | null }).plate_fix_appointment_id ?? null,
    // 海关把柜子拉去查验了（Transfer for Exam）。服务端从柜子监控的扣柜标签算出来，
    // On Hold 列表靠它把这类柜子单独分组。
    //
    // 这里和 utils/backendTaskToContainer.ts 是两个各自维护的转换函数，字段要两边
    // 都加 —— 只加一边的后果就是本次上线时的样子：接口返回得好好的，页面上什么
    // 都没发生，因为派单板走的是这一个。
    customsExam: !!(task as BackendTask & { customs_exam?: boolean }).customs_exam,
    // 查验站那边的免费期最后一天。柜子进 CBP Hold 之后码头那条监控就停了，
    // 上面那个 lfd 从此冻住不动 —— 这段时间还在计钱的是 PCC 这个日子。
    // 同样是「两份转换函数都要加」的字段之一，柜子管理页走的是这一个。
    pccLastFreeDay:
      (task as BackendTask & { pcc_lfd?: string | null }).pcc_lfd || null,
    // 只差一笔码头费就能放行 —— 调度员付掉当场就能提，是 On Hold 里唯一
    // 能马上动手的一类。分组时排在最前面。
    awaitingGateFee: !!(task as BackendTask & { awaiting_gate_fee?: boolean }).awaiting_gate_fee,
    // 这笔费我们已经付过了（eModal 收据已回填）—— 图标据此从绿变灰。
    // 同样是「两份转换函数都要加」：上一版只加了 backendTaskToContainer 那一份，
    // 于是调度台这边一刷新图标就又变回绿的，等于催调度员再付一次。
    gateFeePaid: !!(task as BackendTask & { gate_fee_paid?: boolean }).gate_fee_paid,
    // 封闭区：柜子在码头但拿不出来；recalledFromDispatch 表示它是从已派单
    // 状态被拉回来的（可能有司机在路上）。
    closedArea: !!(task as BackendTask & { closed_area?: boolean }).closed_area,
    recalledFromDispatch: !!(task as BackendTask & { recalled_from_dispatch?: boolean }).recalled_from_dispatch,
    // 提柜那张票已经来不及了（封闭区解除时判的）—— Route 列显示 Rebook。
    // 同样是「两份转换函数都要加」的字段之一。
    portApptNeedsRebook:
      !!(task as BackendTask & { port_appt_needs_rebook?: boolean }).port_appt_needs_rebook,
    driver: task.driver_name || 'Unassigned',
    driverAvatar: '/placeholder.svg?height=32&width=32',
    portAppointment: task.port_appointment_time || '',
    // 提柜那一程的出闸日，服务端顺带查出来的（见 PICKUP_GATE_OUT_JOIN）。柜子管理
    // 列表的 Loading / Empty 行靠它显示「出来几天了」，也就是滞期费在跑几天。
    //
    // 这里和 utils/backendTaskToContainer.ts 是两个各自维护的转换函数，字段要两边
    // 都加 —— 柜子管理页走的是这一个。
    pickupGateOutTime:
      (task as BackendTask & { pickup_gate_out_time?: string | null }).pickup_gate_out_time || undefined,
    pickupGateOutSource:
      (task as BackendTask & { pickup_gate_out_source?: ContainerTask['pickupGateOutSource'] })
        .pickup_gate_out_source || undefined,
    lfd: task.lfd || '',
    shipper: task.shipper_name || '',
    shipperId: task.shipper_id,  // 保存 shipper ID 用于后续更新
    shipperContact: task.shipper_name ? {
      name: task.shipper_contact_name || '',
      phone: task.shipper_contact_phone || '',
      email: task.shipper_contact_email || '',
      address: task.shipper_address || ''
    } : undefined,
    warehouse: task.warehouse_name || '',
    portId: task.port_id,  // 保存 port ID 用于后续更新
    warehouseId: task.warehouse_id,  // 保存 warehouse ID 用于后续更新
    warehouseAddress: task.warehouse_address || '',
    warehouseContact: task.warehouse_contact_name ? {
      name: task.warehouse_contact_name || '',
      phone: task.warehouse_contact_phone || '',
      email: task.warehouse_contact_email || ''
    } : undefined,
    warehouseAppointment: task.warehouse_appointment || '',
    internalNote: task.dispatcher_notes || '',
    containerSize: task.container_size || '40ft',
    // 没有值就是「不知道」，不再兜一个 'dry' —— 兜出来的干货柜会被界面当成事实画出来。
    containerType: (task.container_type || undefined) as ContainerTask['containerType'],
    // 要哪种发电机。和 backendTaskToContainer 那份保持一致 —— 两份转换各自
    // 维护，漏掉一份就是「某些页面有图标、某些页面没有」。
    gensetType: (task.genset_type || undefined) as ContainerTask['gensetType'],
    chassisNumber: task.chassis_number || '',
    chassisId: task.chassis_id,
    chassisCompany: task.chassis_company || undefined,
    importExport: (task.import_export || 'import') as ContainerTask['importExport'],
    originPort: task.port_name || '',
    destinationPort: '',
    bookingReference: '',
    vesselInformation: '',
    specialInstructions: task.dispatcher_instructions || '',
    // 标签数组是存量监控行唯一有的东西；结构化清单（details）是新行才有的原话。
    // 两样都带上，界面优先显示原话、没有才退回标签。
    holdReasons: holdInfo?.tags ?? [],
    holdDetails: holdInfo?.details ?? [],
    terminalHoldCodes: holdInfo?.terminal_codes ?? null,
    holdCheckedAt: holdInfo?.checked_at ?? null,
    holdMonitorActive: holdInfo?.monitor_status === 'active',
    holdReasonDetail: '',
    pauseReasonDetail: task.pause_reason_detail || '',
    etaResume: task.eta_resume || '',
    // Appointment enhancement fields
    portAppointmentEnd: task.port_appointment_end || '',
    portAppointmentStatus: task.port_appointment_status || 'pending',
    warehouseAppointmentStatus: task.warehouse_appointment_status || 'pending',
    // Pause tracking fields
    pausedAt: task.paused_at || '',
    pausedFromStatus: task.paused_from_status || '',
    pauseReason: task.pause_reason || '',
    pauseLocation: task.pause_location || '',
    totalPauseMinutes: task.total_pause_minutes || 0,
    portContacts: task.port_contact_name ? {
      ssa: { name: task.port_contact_name, phone: task.port_contact_phone || '', email: task.port_contact_email || '', address: task.port_address || '' },
      trapac: undefined,
      ets: undefined
    } : undefined,
    // Driver extended info
    driverEmail: task.driver_email || '',
    driverPhone: task.driver_phone || '',
    driverLicense: task.driver_license || '',
    truckPlate: task.truck_plate || '',
    driverUserId: task.driver_user_id,
    gateCode: task.gate_code || '',
    pin: '',
    portPin: task.port_pin || undefined,
    warehousePin: task.warehouse_pin || undefined,
    portContainerLocation: task.port_container_location || undefined,
    warehouseContainerLocation: task.warehouse_container_location || undefined,
    warehouseAppointmentRequired: task.warehouse_appointment_required ?? undefined,
    ssaBooking: '',
    sealNumber: task.seal_number || '',
    oaDocumentUrl: '',
    boDocumentUrl: '',
    currentLocation: task.current_location || '',
    availableTimes: '',
    dispatcherToDriverInstructions: task.to_driver_notes || '',
    processingTimeline: [],
    nextActions: task.next_action ? [task.next_action] : [],
    lastUpdate: task.updated_at || task.created_at,
    driverStatus: task.driver_status || '',
    driverContactStatus: '',
    driverLastSeen: '',
    // Additional fields
    shipOwner: task.shipping_line || '',
    sizeType: task.container_size || '',
    dryReefer: (task.container_type || undefined) as 'dry' | 'reefer' | undefined,
    weight: task.weight || task.container_weight || 0,
    isOverweight: task.is_overweight || false,
    bol: task.bol_number || '',
    bookingNumber: task.booking_number || '',
    warehouseName: task.warehouse_name || '',
    warehouseShortName: task.warehouse_short_name || '',
    warehouseCity: task.warehouse_city || '',
    warehouseState: task.warehouse_state || '',
    portName: task.port_name || '',
    portShortName: task.port_short_name || '',
    portCity: task.port_city || '',
    portState: task.port_state || '',
    portAddress: task.port_address || '',
    // Yard fields (for Route Node display when destination is yard)
    yardId: task.yard_id || null,
    yardName: task.yard_name || '',
    yardShortName: task.yard_short_name || '',
    yardCity: task.yard_city || '',
    yardState: task.yard_state || '',
    yardAddress: task.yard_address || '',
    yardContact: task.yard_contact_name ? {
      name: task.yard_contact_name || '',
      phone: task.yard_contact_phone || '',
      email: task.yard_contact_email || ''
    } : undefined,
    apptInfo: task.port_appointment_time || '',
    type: (task.port_location_type ?? undefined) as 'Decked' | 'Wheeled' | undefined,
    location: task.current_location || '',
    warehouseApptInfo: task.warehouse_appointment || '',
    warehouseApptId: task.warehouse_appointment_number || '',
    appointmentNumber: task.warehouse_appointment_number || '',
    // Three-party role fields (from Order)
    orderingPartyType: task.ordering_party_type,
    orderingPartyId: task.ordering_party_id,
    orderingPartyName: task.ordering_party_name,
    orderingPartyContact: (task.ordering_party_contact_name || task.ordering_party_contact_phone || task.ordering_party_contact_email || task.ordering_party_address) ? {
      name: task.ordering_party_contact_name || '',
      phone: task.ordering_party_contact_phone || '',
      email: task.ordering_party_contact_email || '',
      address: task.ordering_party_address || ''
    } : undefined,
    billToType: task.bill_to_type,
    billToId: task.bill_to_id,
    billToName: task.bill_to_name,
    billToContact: (task.bill_to_contact_name || task.bill_to_contact_phone || task.bill_to_contact_email || task.bill_to_address) ? {
      name: task.bill_to_contact_name || '',
      phone: task.bill_to_contact_phone || '',
      email: task.bill_to_contact_email || '',
      address: task.bill_to_address || ''
    } : undefined,
    deliverToType: task.deliver_to_type,
    deliverToId: task.deliver_to_id,
    deliverToName: task.deliver_to_name,
    deliverToContact: (task.deliver_to_contact_name || task.deliver_to_contact_phone || task.deliver_to_contact_email || task.deliver_to_address) ? {
      name: task.deliver_to_contact_name || '',
      phone: task.deliver_to_contact_phone || '',
      email: task.deliver_to_contact_email || '',
      address: task.deliver_to_address || ''
    } : undefined,
    // Shipper polymorphic fields (货主多态)
    shipperType: task.shipper_type,
    // Use polymorphic name first, fall back to legacy shipper_name
    shipperName: task.shipper_polymorphic_name || task.shipper_name || '',
    // Task location polymorphic fields
    originType: task.origin_type,
    originId: task.origin_id,
    originName: task.origin_name,
    originShortName: task.origin_short_name || '',
    destinationType: task.destination_type,
    destinationId: task.destination_id,
    destinationName: task.destination_name,
    destinationShortName: task.destination_short_name || '',
    // Pending Yard Storage fields (for Liveload route generation)
    pendingYardStorage: task.pending_yard_storage || false,
    pendingDeliveryDestinationType: task.pending_delivery_destination_type as LocationType | undefined,
    pendingDeliveryDestinationId: task.pending_delivery_destination_id,
    // PCC Inspection fields
    pccLocationId: task.pcc_location_id || null,
    pccLocationName: task.pcc_location_name || '',
    pccLocationAddress: task.pcc_location_address || '',
    pccLocationCity: task.pcc_location_city || '',
    pccHoldAt: task.pcc_hold_at || '',
    pccHoldReason: task.pcc_hold_reason || '',
    pendingPccInspection: task.pending_pcc_inspection || false,
    // Order info
    orderId: task.order_id,
    orderDisplayId: task.order_display_id || '',
    orderStatus: task.order_status || '',
    orderType: task.order_type || '',
    orderCreatedAt: task.order_created_at || '',
    // Route nodes JSON (for accurate display after reroute, especially Liveload with different ports)
    routeNodes: (typeof task.route_nodes === 'string' ? undefined : task.route_nodes) as RouteNode[] | undefined,
    routeNodeCount: task.route_node_count,
    // Return location fields (for Liveload tasks - where empty container returns to)
    returnLocationType: task.return_location_type as LocationType | undefined,
    returnLocationId: task.return_location_id,
    returnLocationPending: task.return_location_pending || false,
    // Resolved return location name fields (from API JOIN)
    returnLocationName: task.return_location_name,
    returnLocationShortName: task.return_location_short_name,
    returnLocationCity: task.return_location_city,
    returnLocationAddress: task.return_location_address,
    // Zone & coordinate fields (for dispatch bobtail preview)
    originZoneId: task.origin_zone_id ?? undefined,
    destinationZoneId: task.destination_zone_id ?? undefined,
    originLat: task.origin_lat ? parseFloat(String(task.origin_lat)) : undefined,
    originLng: task.origin_lng ? parseFloat(String(task.origin_lng)) : undefined,
    destinationLat: task.destination_lat ? parseFloat(String(task.destination_lat)) : undefined,
    destinationLng: task.destination_lng ? parseFloat(String(task.destination_lng)) : undefined,
    returnLocationZoneId: task.return_location_zone_id ?? undefined,
    returnLocationLat: task.return_location_lat ? parseFloat(String(task.return_location_lat)) : undefined,
    returnLocationLng: task.return_location_lng ? parseFloat(String(task.return_location_lng)) : undefined,
    // Distance & Bobtail fields
    estimatedDistanceMiles: task.estimated_distance_miles ? parseFloat(String(task.estimated_distance_miles)) : undefined,
    actualDistanceMiles: task.actual_distance_miles ? parseFloat(String(task.actual_distance_miles)) : undefined,
    isBobtailLeg: task.is_bobtail_leg || false,
    bobtailFromZoneId: task.bobtail_from_zone_id ?? undefined,
    bobtailToZoneId: task.bobtail_to_zone_id ?? undefined,
    // 司机卡里手排的位次（迁移 192）——见 backendTaskToContainer 里同名字段。
    driverSortDay: task.driver_sort_day ?? null,
    driverSortOrder: task.driver_sort_order ?? null,
    // 收尾那一天（迁移 212）——见 backendTaskToContainer 里同名字段。
    completedDay: task.completed_day ?? undefined
  };
};

/**
 * 将前端 ContainerTask 数据转换为后端更新格式
 *
 * 注意：tasks 表直接存储的字段可以直接更新
 * container_number, shipping_line, container_size, container_type 等字段
 * 存在于 containers 表中，需要通过 container_id 关联更新
 */
export const transformFrontendToBackend = (task: ContainerTask): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  // === Tasks 表直接字段 ===

  // 状态和类型
  //
  // statusBaseline = 这份表单打开时看到的 status。
  // 没动过状态就压根不发 status —— 表单在手上放几分钟是常事，期间别人可能已经
  // 派了车（status → assigned），再把表单里那份旧状态发回去就会把它抹掉。
  // 动过才发，并附上 expected_status 让后端校验这份表单是不是基于最新版本。
  const statusUntouched =
    task.statusBaseline !== undefined && task.status === task.statusBaseline;
  if (task.status !== undefined && !statusUntouched) {
    result.status = mapFrontendStatusToBackend(task.status);
    if (task.statusBaseline !== undefined) {
      result.expected_status = mapFrontendStatusToBackend(task.statusBaseline);
    }
  }
  if (task.taskType !== undefined) {
    result.task_type = mapFrontendTaskTypeToBackend(task.taskType);
  }
  if (task.taskSubtype !== undefined) {
    result.task_subtype = task.taskSubtype;
  }

  // 调度员备注和指示
  if (task.internalNote !== undefined) {
    result.dispatcher_notes = task.internalNote;
  }
  if (task.specialInstructions !== undefined) {
    result.dispatcher_instructions = task.specialInstructions;
  }
  if (task.dispatcherToDriverInstructions !== undefined) {
    result.to_driver_notes = task.dispatcherToDriverInstructions;
  }

  // 位置和门码
  if (task.currentLocation !== undefined) {
    result.current_location = task.currentLocation;
  }
  if (task.location !== undefined) {
    result.current_location = task.location;
  }
  if (task.gateCode !== undefined) {
    result.gate_code = task.gateCode;
  }
  if (task.portPin !== undefined) {
    result.port_pin = task.portPin || null;
  }
  if (task.warehousePin !== undefined) {
    result.warehouse_pin = task.warehousePin || null;
  }
  if (task.portContainerLocation !== undefined) {
    result.port_container_location = task.portContainerLocation || null;
  }
  if (task.warehouseContainerLocation !== undefined) {
    result.warehouse_container_location = task.warehouseContainerLocation || null;
  }
  // warehouse_appointment_required intentionally NOT written back:
  // backend COALESCE returns the effective value (task → warehouse → true),
  // so frontend can't distinguish "task override" from "inherited from warehouse".
  // Writing back would hard-copy the warehouse setting into the task column and
  // break inheritance. Only an explicit UI toggle should persist this field.

  // 封条号
  if (task.sealNumber !== undefined) {
    result.seal_number = task.sealNumber;
  }

  // 时间和预约 (空字符串和 "Pending" 转换为 null)
  if (task.lfd !== undefined) {
    result.lfd = (task.lfd && task.lfd !== 'Pending') ? task.lfd : null;
  }
  if (task.portAppointment !== undefined) {
    result.port_appointment_time = (task.portAppointment && task.portAppointment !== 'Pending') ? task.portAppointment : null;
  }
  if (task.warehouseAppointment !== undefined) {
    result.warehouse_appointment = (task.warehouseAppointment && task.warehouseAppointment !== 'Pending') ? task.warehouseAppointment : null;
  }
  if (task.appointmentNumber !== undefined) {
    result.warehouse_appointment_number = task.appointmentNumber || null;
  }

  // 下一步动作
  if (task.nextActions !== undefined && task.nextActions.length > 0) {
    result.next_action = task.nextActions[0];
  }

  // 重量 (tasks 表有 weight 字段)
  if (task.weight !== undefined) {
    result.weight = task.weight;
  }

  // 超重标记
  if (task.isOverweight !== undefined) {
    result.is_overweight = task.isOverweight;
  }

  // 进出口类型
  if (task.importExport !== undefined) {
    result.import_export = task.importExport;
  }

  // Hold/Pause 相关
  if (task.holdReasonDetail !== undefined) {
    result.hold_reason = task.holdReasonDetail;
  }
  if (task.pauseReasonDetail !== undefined) {
    result.pause_reason = task.pauseReasonDetail;
  }
  if (task.etaResume !== undefined) {
    result.eta_resume = task.etaResume;
  }

  // 港口位置类型
  if (task.type !== undefined) {
    result.port_location_type = task.type ?? null;
  }

  // === Chassis ID ===
  // 如果前端传递了 chassisId，直接使用
  if (task.chassisId !== undefined) {
    result.chassis_id = task.chassisId || null;
  }

  // === Port ID ===
  // 如果前端传递了 portId，转换为后端字段名
  if (task.portId !== undefined) {
    result.port_id = task.portId || null;
  }

  // === Shipper ID ===
  // 如果前端传递了 shipperId，转换为后端字段名
  if (task.shipperId !== undefined) {
    result.shipper_id = task.shipperId || null;
  }
  // Shipper polymorphic type
  if (task.shipperType !== undefined) {
    result.shipper_type = task.shipperType || null;
  }

  // === Warehouse ID ===
  // 如果前端传递了 warehouseId，转换为后端字段名
  if (task.warehouseId !== undefined) {
    result.warehouse_id = task.warehouseId || null;
  }

  // === Container 相关字段 (需要跨表更新) ===
  // 这些字段会在后端 update 方法中同步更新到 containers 表
  if (task.shippingLine !== undefined) {
    result.shipping_line = task.shippingLine || null;
  }
  if (task.containerSize !== undefined) {
    result.container_size = task.containerSize || null;
  }
  if (task.containerType !== undefined) {
    result.container_type = task.containerType || null;
  }
  if (task.containerNumber !== undefined) {
    result.container_number = task.containerNumber || null;
  }

  // === 要哪种发电机（跨表写入: →orders，migration 201）===
  // 只在用户真点过图标之后才带上。undefined 是「没人说过」那一态（存量订单），
  // 无条件送 'none' 会把它悄悄改写成「客户说了不要」。
  if (task.gensetType !== undefined) {
    result.genset_type = task.gensetType;
  }

  // === BOL / Booking Number (跨表写入: bol→containers, booking→orders) ===
  if (task.bol !== undefined) {
    result.bol_number = task.bol || null;
  }
  if (task.bookingNumber !== undefined) {
    result.booking_number = task.bookingNumber || null;
  }

  // === Three-party role fields (三问判定法) ===
  // Note: These are typically set at order level, not task level
  // But we support updating them through task for convenience
  if (task.orderingPartyType !== undefined) {
    result.ordering_party_type = task.orderingPartyType || null;
  }
  if (task.orderingPartyId !== undefined) {
    result.ordering_party_id = task.orderingPartyId || null;
  }
  if (task.billToType !== undefined) {
    result.bill_to_type = task.billToType || null;
  }
  if (task.billToId !== undefined) {
    result.bill_to_id = task.billToId || null;
  }
  if (task.deliverToType !== undefined) {
    result.deliver_to_type = task.deliverToType || null;
  }
  if (task.deliverToId !== undefined) {
    result.deliver_to_id = task.deliverToId || null;
  }

  // === Task location polymorphic fields ===
  if (task.originType !== undefined) {
    result.origin_type = task.originType || null;
  }
  if (task.originId !== undefined) {
    result.origin_id = task.originId || null;
  }
  if (task.destinationType !== undefined) {
    result.destination_type = task.destinationType || null;
  }
  if (task.destinationId !== undefined) {
    result.destination_id = task.destinationId || null;
  }

  return result;
};

// ==================== Stats Interface ====================

interface DispatchStats {
  total: number;
  vessel: number;
  onHold: number;
  released: number;
  unassigned: number;  // v3.0: Waiting to be assigned to driver
  assigned: number;    // v3.0: Assigned to driver, waiting to start
  picking: number;
  delivering: number;
  onStopYard: number;
  loading: number;
  empty: number;
  loaded: number;      // v3.0: Export - loaded, waiting to return
  returning: number;
  completed: number;
  cancelled: number;   // v3.0: Task cancelled
  pause: number;
  yardStored: number;  // v3.0: Container stored in yard, waiting for appt
  pccHold: number;     // CBP inspection hold
  uncompleted: number; // Uncompleted tasks
  marked: number;
}

// ==================== Hook ====================

interface UseDispatchTasksOptions {
  autoFetch?: boolean;
  initialFilters?: TaskFilters;
  // Phase 4: 自动刷新配置
  autoRefresh?: boolean;
  refreshInterval?: number; // 毫秒，默认 30000 (30秒)
  // Phase 5: 实时更新配置
  enableRealtime?: boolean; // 是否启用 WebSocket 实时更新
}

interface UseDispatchTasksReturn {
  // Data
  tasks: ContainerTask[];
  drivers: string[];
  driversData: DriverData[];
  shippers: { name: string; shortName?: string }[];
  destinations: WarehouseOption[];
  ports: PortOption[];
  stats: DispatchStats;
  // State
  loading: boolean;
  error: string | null;
  // Phase 4: 自动刷新状态
  isAutoRefreshEnabled: boolean;
  lastRefreshTime: Date | null;
  // Phase 5: 实时连接状态
  isRealtimeConnected: boolean;
  lastRealtimeUpdate: Date | null;
  // Actions
  fetchTasks: (filters?: TaskFilters) => Promise<void>;
  fetchStats: () => Promise<void>;  // Phase 2: 单独获取统计
  updateTask: (taskId: number, data: Partial<ContainerTask>) => Promise<boolean>;
  updateTaskStatus: (taskId: number, status: string, currentStatus?: string, unlockReason?: string) => Promise<boolean>;
  assignDriver: (taskId: number, driverId: number, truckId?: number) => Promise<boolean>;
  // B3 Fix Plate. Re-exported from the hook's own useFixPlateFlow instance so
  // task rows can drive the flow without each mounting their own copy — every
  // instance registers an 'appointment_updated' socket listener.
  startFixPlate: (appointmentId: number) => Promise<{ ok: boolean; error?: string }>;
  // Gate Fee — same reasoning as startFixPlate above.
  startGateFee: (params: { taskId?: number | string; orderId?: number }) => Promise<{ ok: boolean; error?: string }>;
  /** 批量付闸口费 —— 勾好的几张任务一次送进同一个 eModal 会话。 */
  startGateFeeBatch: (taskIds: number[]) => Promise<{ ok: boolean; error?: string }>;
  refetch: () => Promise<void>;
  // Phase 4: 自动刷新控制
  toggleAutoRefresh: () => void;
  setRefreshInterval: (interval: number) => void;
  // Phase 4: 批量操作
  batchUpdateStatus: (taskIds: string[], status: string) => Promise<boolean>;
  batchAssignDriver: (taskIds: string[], driverId: number, truckId?: number) => Promise<boolean>;
  batchDeleteTasks: (taskIds: string[]) => Promise<boolean>;
  // Pagination
  total: number;
  limit: number;
  offset: number;
}

export const useDispatchTasks = (options: UseDispatchTasksOptions = {}): UseDispatchTasksReturn => {
  const {
    autoFetch = true,
    initialFilters,
    autoRefresh = false,
    refreshInterval: initialRefreshInterval = 30000, // 默认30秒
    enableRealtime = true // Phase 5: 默认启用实时更新
  } = options;

  // Use stable reference for empty filters to prevent infinite loops
  const initialFiltersJson = JSON.stringify(initialFilters);
  const stableFilters = useMemo(
    () => initialFilters || EMPTY_FILTERS,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialFiltersJson]
  );

  // B3 Fix Plate. Mounted once here and handed down to the rows rather than per
  // row — every instance registers an 'appointment_updated' socket listener.
  // Safe to mount unconditionally: it does nothing until startFixPlate is called.
  const { startFixPlate } = useFixPlateFlow();
  const { startGateFee, startGateFeeBatch } = useGateFeeFlow();

  // Task data
  const [tasks, setTasks] = useState<ContainerTask[]>([]);

  // 收据一回来就把这张任务的图标从绿改成灰。
  //
  // 两点都是故意的：一，判据是收据不是码头撤标 —— 付完到撤标可能隔几个小时，
  // 那几个小时里图标亮着就是在催人重复付款。二，直接听广播事件而不是走
  // useGateFeeFlow 的回调 —— 那个回调只对「自己点的那一单」触发，别的调度员
  // 的屏幕上会一直绿着，而重复付款正是别人看见绿色才发生的。
  useEffect(() => {
    const handle = (raw: unknown) => {
      const { orderId } = (raw || {}) as { orderId?: number };
      if (!orderId) return;
      setTasks(prev => prev.map(t => (t.orderId === orderId ? { ...t, gateFeePaid: true } : t)));
    };
    socketService.on('gate_fee_paid', handle);
    return () => { socketService.off('gate_fee_paid', handle); };
  }, []);

  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);

  // Phase 4: 自动刷新状态
  const [isAutoRefreshEnabled, setIsAutoRefreshEnabled] = useState(autoRefresh);
  const [refreshInterval, setRefreshIntervalState] = useState(initialRefreshInterval);
  const [lastRefreshTime, setLastRefreshTime] = useState<Date | null>(null);

  // Dropdown data
  const [drivers, setDrivers] = useState<string[]>([]);
  const [driversData, setDriversData] = useState<DriverData[]>([]);
  const [shippers, setShippers] = useState<{ name: string; shortName?: string }[]>([]);
  const [destinations, setDestinations] = useState<WarehouseOption[]>([]);
  const [ports, setPorts] = useState<PortOption[]>([]);

  // Stats
  const [stats, setStats] = useState<DispatchStats>({
    total: 0,
    vessel: 0,
    onHold: 0,
    released: 0,
    unassigned: 0,
    assigned: 0,
    picking: 0,
    delivering: 0,
    onStopYard: 0,
    loading: 0,
    empty: 0,
    loaded: 0,
    returning: 0,
    completed: 0,
    cancelled: 0,
    pause: 0,
    yardStored: 0,
    pccHold: 0,
    uncompleted: 0,
    marked: 0
  });

  // Loading & Error
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Phase 5: Real-time update handlers
  const handleTaskUpdate = useCallback((data: { taskId: number; status: string; updatedFields?: string[] }) => {
    setTasks(prevTasks =>
      prevTasks.map(task =>
        task.id === String(data.taskId)
          ? { ...task, status: mapBackendStatusToFrontend(data.status) as ContainerTask['status'] }
          : task
      )
    );
  }, []);

  const handleTaskCreated = useCallback((data: { taskId: number; task_id?: string; status: string; container_number?: string }) => {
    // Fetch the full task data (with JOINs) and add to list
    taskAPI.getById(data.taskId).then(task => {
      const transformedTask = transformBackendToFrontend(task);
      setTasks(prevTasks => {
        // 去重：检查是否已存在相同 ID 的任务
        const exists = prevTasks.some(t => t.id === transformedTask.id);
        if (exists) {
          return prevTasks;
        }
        return [transformedTask, ...prevTasks];
      });
      setTotal(prev => prev + 1);
    }).catch(err => console.error('[Dispatch] Failed to fetch new task:', err));
  }, []);

  const handleTaskCompleted = useCallback((data: { taskId: number; status: string }) => {
    setTasks(prevTasks =>
      prevTasks.map(task =>
        task.id === String(data.taskId)
          ? { ...task, status: 'completed' as ContainerTask['status'] }
          : task
      )
    );
  }, []);

  const handleDriverAssigned = useCallback((data: { taskId: number; driverId: number; driverName: string }) => {
    setTasks(prevTasks =>
      prevTasks.map(task =>
        task.id === String(data.taskId)
          ? { ...task, driver: data.driverName }
          : task
      )
    );
  }, []);

  const handleBatchUpdate = useCallback((data: { taskIds: number[]; status?: string; driverName?: string; action: string }) => {
    const taskIdStrings = data.taskIds.map(String);
    setTasks(prevTasks =>
      prevTasks.map(task => {
        if (taskIdStrings.includes(task.id)) {
          const updates: Partial<ContainerTask> = {};
          if (data.status) {
            updates.status = mapBackendStatusToFrontend(data.status) as ContainerTask['status'];
          }
          if (data.driverName) {
            updates.driver = data.driverName;
          }
          return { ...task, ...updates };
        }
        return task;
      })
    );
  }, []);

  /**
   * 计算统计数据（基于当前任务列表）
   */
  const calculateStats = useCallback((taskList: ContainerTask[]): DispatchStats => {
    const statsCounts: DispatchStats = {
      total: taskList.length,
      vessel: 0,
      onHold: 0,
      released: 0,
      unassigned: 0,
      assigned: 0,
      picking: 0,
      delivering: 0,
      onStopYard: 0,
      loading: 0,
      empty: 0,
      loaded: 0,
      returning: 0,
      completed: 0,
      cancelled: 0,
      pause: 0,
      yardStored: 0,
      pccHold: 0,
      uncompleted: 0,
      marked: 0
    };

    taskList.forEach(task => {
      switch (task.status) {
        case 'on-vessel': statsCounts.vessel++; break;
        case 'on-hold': statsCounts.onHold++; break;
        case 'released': statsCounts.released++; break;
        case 'unassigned': statsCounts.unassigned++; break;
        case 'assigned': statsCounts.assigned++; break;
        case 'picking': statsCounts.picking++; break;
        case 'delivering': statsCounts.delivering++; break;
        case 'on-stop': statsCounts.onStopYard++; break;
        case 'loading': statsCounts.loading++; break;
        case 'empty': statsCounts.empty++; break;
        case 'loaded': statsCounts.loaded++; break;
        case 'returning': statsCounts.returning++; break;
        case 'completed': statsCounts.completed++; break;
        case 'cancelled': statsCounts.cancelled++; break;
        case 'paused': statsCounts.pause++; break;
        case 'yard-stored': statsCounts.yardStored++; break;
        case 'pcc-hold': statsCounts.pccHold++; break;
        case 'uncompleted': statsCounts.uncompleted++; break;
      }
    });

    return statsCounts;
  }, []);

  /**
   * 获取统计数据 (Phase 2 - 使用后端 API)
   */
  const fetchStats = useCallback(async () => {
    try {
      const statsData = await taskAPI.getStatsByStatus();
      // Ensure all fields have numeric defaults to prevent NaN in UI
      setStats(prev => ({
        ...prev,
        ...Object.fromEntries(
          Object.entries(statsData).map(([k, v]) => [k, typeof v === 'number' ? v : 0])
        )
      } as DispatchStats));
    } catch (err) {
      console.error('Failed to fetch stats from API, falling back to local calculation:', err);
      // Fallback to local calculation if API fails
    }
  }, []);

  /**
   * 获取任务列表
   */
  const fetchTasks = useCallback(async (filters: TaskFilters = {}) => {
    setLoading(true);
    setError(null);

    try {
      const response = await taskAPI.getAll({
        limit: 10000,
        offset: 0,
        ...stableFilters,
        ...filters
      });

      const transformedTasks = response.data.map(transformBackendToFrontend);
      setTasks(transformedTasks);
      setTotal(response.total);
      setLimit(response.limit);
      setOffset(response.offset);

      // Calculate stats locally as fallback
      const calculatedStats = calculateStats(transformedTasks);
      setStats(calculatedStats);

    } catch (err) {
      console.error('Failed to fetch tasks:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch tasks');
      // Keep existing tasks on error
    } finally {
      setLoading(false);
    }
  }, [stableFilters, calculateStats]);

  /**
   * 获取下拉选项数据
   */
  const fetchDropdownData = useCallback(async () => {
    try {
      const [driversRes, shippersRes, warehousesRes, portsRes] = await Promise.all([
        driverApi.getAllRaw().catch(() => ({ data: [] })),
        shipperAPI.getAll({ limit: 500 }).catch(() => ({ data: [] })),
        warehouseClientAPI.getAll({ limit: 500 }).catch(() => ({ data: [] })),
        getAllPorts({ limit: 500 }).catch(() => ({ data: [] }))
      ]);

      // Driver names for dropdown
      const driverItems = (driversRes.data || []) as DriverItem[];
      const driverNames = driverItems.map(d => d.full_name || d.name || '');
      setDrivers(driverNames.filter(Boolean));

      // Full driver data for DriverData type
      const fullDriverData: DriverData[] = driverItems.map(d => ({
        id: String(d.id),
        name: d.full_name || d.name || '',
        avatar: d.avatar_url || '/placeholder.svg?height=32&width=32',
        status: d.status || 'Available',
        location: d.current_location || '',
        rating: d.rating || 5,
        currentTasks: [],
        phone: d.phone || '',
        email: d.email || '',
        company: d.company || '',
        plateLicense: d.truck_plate || '',
        truckNumber: d.truck_id ? `T-${String(d.truck_id).padStart(4, '0')}` : ''
      }));
      setDriversData(fullDriverData);

      // Shipper names (backend getAll returns camelCase aliases)
      const shipperItems = (shippersRes.data || []) as ShipperItem[];
      setShippers(shipperItems.map(s => ({ name: s.companyName || s.name || '', shortName: s.shortName || s.short_name })).filter(s => s.name));

      // Warehouse dropdown options
      const warehouseItems = (warehousesRes.data || []) as WarehouseItem[];
      const warehouseOptions: WarehouseOption[] = warehouseItems.map(w => ({
        id: String(w.id),
        name: w.warehouseName || '',
        shortName: w.shortName || ''
      }));
      setDestinations(warehouseOptions.filter(w => w.name || w.shortName));

      // Port options (backend returns snake_case)
      const portItems = (portsRes.data || []) as PortItem[];
      const portOptions: PortOption[] = portItems.map(p => ({
        id: String(p.id),
        name: p.portName || '',
        shortName: p.shortName || '',
        code: p.portCode || ''
      }));
      setPorts(portOptions.filter(p => p.name || p.shortName));

    } catch (err) {
      console.error('Failed to fetch dropdown data:', err);
      // Use empty arrays on error - don't break the page
    }
  }, []);

  // Phase 5: Handle order updated (for syncing three-party roles changes)
  // Placed here after fetchTasks and fetchStats are defined
  const handleOrderUpdated = useCallback((data: { orderId: string; taskIds: number[]; updatedFields?: string[] }) => {
    // Refetch tasks to get updated order data (three-party roles, etc.)
    fetchTasks();
    fetchStats();
  }, [fetchTasks, fetchStats]);

  // Phase 5: Initialize real-time connection
  const { isConnected: isRealtimeConnected, lastUpdate: lastRealtimeUpdate } = useRealtimeTasks({
    enabled: enableRealtime,
    onTaskUpdate: handleTaskUpdate,
    onTaskCreated: handleTaskCreated,
    onTaskCompleted: handleTaskCompleted,
    onDriverAssigned: handleDriverAssigned,
    onBatchUpdate: handleBatchUpdate,
    onOrderUpdate: handleOrderUpdated
  });

  /**
   * 更新任务
   */
  const updateTask = useCallback(async (taskId: number, data: Partial<ContainerTask>): Promise<boolean> => {
    try {
      const backendData = transformFrontendToBackend(data as ContainerTask);
      const updated = await taskAPI.update(taskId, backendData as Record<string, string | number | undefined>);

      // 后端拒收了这次保存里的某个字段（目前只有"表单过期，状态没保存"这一种）。
      // 以前这种情况静悄悄地成功返回，调度员完全不知道状态没写进去。
      const saveWarnings = (updated as { _warnings?: string[] } | null)?._warnings;
      if (saveWarnings?.length) {
        saveWarnings.forEach(w => message.warning(w, 8));
      }

      // 被拒的 status 不能写进本地状态，否则界面上会显示一个数据库里并不存在的状态。
      const applied: Partial<ContainerTask> = { ...data };
      delete applied.statusBaseline;
      if (saveWarnings?.length) delete applied.status;

      // Update local state
      setTasks(prevTasks =>
        prevTasks.map(task =>
          task.id === String(taskId) ? { ...task, ...applied } : task
        )
      );

      return true;
    } catch (err) {
      // Extract actual error message from axios response
      const axiosErr = err as { response?: { data?: { message?: string; detail?: string } }; message?: string };
      const responseData = axiosErr.response?.data;
      const errorMessage = responseData?.message || axiosErr.message || 'Failed to update task';
      const errorDetail = responseData?.detail ? ` (${responseData.detail})` : '';
      const fullError = errorMessage + errorDetail;
      console.error('Failed to update task:', err);
      console.error('Backend error details:', responseData);
      setError(fullError);
      throw new Error(fullError); // Re-throw with actual message for caller to handle
    }
  }, []);

  /**
   * 更新任务状态 (Phase 2 - 使用专用状态 API)
   * 自动处理终端状态 (cancelled/completed) 的解锁
   */
  const updateTaskStatus = useCallback(async (
    taskId: number,
    status: string,
    currentStatus?: string,
    unlockReason?: string
  ): Promise<boolean> => {
    try {
      const backendStatus = mapFrontendStatusToBackend(status);
      const backendCurrentStatus = currentStatus ? mapFrontendStatusToBackend(currentStatus) : undefined;

      // 使用智能状态更新：自动检测终端状态并调用解锁 API
      await taskAPI.smartUpdateStatus(taskId, backendStatus, backendCurrentStatus || '', unlockReason);

      // Update local state
      setTasks(prevTasks =>
        prevTasks.map(task =>
          task.id === String(taskId)
            ? { ...task, status: status as ContainerTask['status'] }
            : task
        )
      );

      // Refresh stats from backend
      fetchStats();

      return true;
    } catch (err) {
      console.error('Failed to update task status:', err);
      setError(err instanceof Error ? err.message : 'Failed to update task status');
      return false;
    }
  }, [fetchStats]);

  /**
   * 分配司机
   */
  const assignDriver = useCallback(async (
    taskId: number,
    driverId: number,
    truckId?: number
  ): Promise<boolean> => {
    try {
      const updatedTask = await taskAPI.assignDriver(taskId, driverId, truckId);

      // Update local state with driver name
      setTasks(prevTasks =>
        prevTasks.map(task =>
          task.id === String(taskId)
            ? {
                ...task,
                driver: updatedTask.driver_name || 'Assigned',
                status: 'picking' as ContainerTask['status']
              }
            : task
        )
      );

      return true;
    } catch (err) {
      console.error('Failed to assign driver:', err);
      setError(err instanceof Error ? err.message : 'Failed to assign driver');
      return false;
    }
  }, []);

  /**
   * 刷新数据
   */
  const refetch = useCallback(async () => {
    await Promise.all([
      fetchTasks(),
      fetchDropdownData(),
      fetchStats()
    ]);
    setLastRefreshTime(new Date());
  }, [fetchTasks, fetchDropdownData, fetchStats]);

  // Phase 4: 自动刷新控制函数
  const toggleAutoRefresh = useCallback(() => {
    setIsAutoRefreshEnabled(prev => !prev);
  }, []);

  const setRefreshInterval = useCallback((interval: number) => {
    setRefreshIntervalState(Math.max(5000, interval)); // 最小5秒
  }, []);

  // Phase 4: 批量更新任务状态
  const batchUpdateStatus = useCallback(async (taskIds: string[], status: string): Promise<boolean> => {
    try {
      const numericIds = taskIds.map(id => parseInt(id)).filter(id => !isNaN(id));
      if (numericIds.length === 0) return false;

      const backendStatus = mapFrontendStatusToBackend(status);
      await taskAPI.batchUpdateStatus(numericIds, backendStatus);

      // Update local state
      setTasks(prevTasks =>
        prevTasks.map(task =>
          taskIds.includes(task.id)
            ? { ...task, status: status as ContainerTask['status'] }
            : task
        )
      );

      // Refresh stats
      fetchStats();
      return true;
    } catch (err) {
      console.error('Failed to batch update status:', err);
      setError(err instanceof Error ? err.message : 'Failed to batch update status');
      return false;
    }
  }, [fetchStats]);

  // Phase 4: 批量分配司机
  const batchAssignDriver = useCallback(async (
    taskIds: string[],
    driverId: number,
    truckId?: number
  ): Promise<boolean> => {
    try {
      const numericIds = taskIds.map(id => parseInt(id)).filter(id => !isNaN(id));
      if (numericIds.length === 0) return false;

      await taskAPI.batchAssignDriver(numericIds, driverId, truckId);

      // Refresh tasks to get updated driver info
      await fetchTasks();
      fetchStats();
      return true;
    } catch (err) {
      console.error('Failed to batch assign driver:', err);
      setError(err instanceof Error ? err.message : 'Failed to batch assign driver');
      return false;
    }
  }, [fetchTasks, fetchStats]);

  // Phase 4: 批量删除任务
  const batchDeleteTasks = useCallback(async (taskIds: string[]): Promise<boolean> => {
    try {
      const numericIds = taskIds.map(id => parseInt(id)).filter(id => !isNaN(id));
      if (numericIds.length === 0) return false;

      await taskAPI.batchDelete(numericIds);

      // Remove from local state
      setTasks(prevTasks => prevTasks.filter(task => !taskIds.includes(task.id)));

      // Refresh stats
      fetchStats();
      return true;
    } catch (err) {
      console.error('Failed to batch delete tasks:', err);
      setError(err instanceof Error ? err.message : 'Failed to batch delete tasks');
      return false;
    }
  }, [fetchStats]);

  // 初始化加载 + 每次重新进入本页时补拉一次
  //
  // autoFetch 由调用方按路由传入 (isMainPage && oauthReady)。离开本页时
  // useRealtimeTasks 的 effect 会清理掉 socket 监听，这段时间内后端发出的
  // task_created / task_updated 没有任何订阅者，事件就此丢失且不会重放。
  // 原本这里用 initializedRef 把拉取永久锁成"只做一次"，于是回到本页时既
  // 收不到补发的事件、也不会重新查一遍 —— 表现就是"在 Order 页面建完单，
  // 任务列表必须手动刷新才出现"。所以再次进入时必须补拉。
  const initializedRef = useRef(false);
  useEffect(() => {
    if (!autoFetch) return;

    if (!initializedRef.current) {
      initializedRef.current = true;
      fetchTasks();
      fetchDropdownData();
      fetchStats(); // Phase 2: 获取后端统计
      setLastRefreshTime(new Date());
      return;
    }

    // 重新进入本页：只补任务和统计，下拉选项数据变动极少，不必重复拉取
    fetchTasks();
    fetchStats();
    setLastRefreshTime(new Date());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFetch]);
  // 对账完了，列表自己重拉一遍。
  //
  // eModal 对账是一次批量改动：它直接改预约和任务的行，**不会**为改到的每
  // 一行单独发 task_updated —— 所以列表订阅的那些事件一条都不会响。结果是
  // 同步转完、提示也弹了，页面却纹丝不动，非得手动刷新一次才看得见。
  // 「对账完成」这一条就是这批改动唯一的信号，把它当成「重拉」的触发即可。
  //
  // 不做增量：这一批可能同时动了时间、闸口码、状态、还新建了任务行，
  // 挨个打补丁既啰嗦又容易漏，而同步是人点出来的、不频繁，重拉一次最稳。
  useEffect(() => {
    const handle = () => {
      // 和页面自己重进时的刷新走同一条：不带筛选参数，筛选是前端做的。
      void fetchTasks();
      void fetchStats();
      setLastRefreshTime(new Date());
    };
    socketService.on('emodal_sync_completed', handle);
    return () => { socketService.off('emodal_sync_completed', handle); };
  }, [fetchTasks, fetchStats]);

  // Phase 5+: Auto-recalculate stats when tasks change (for real-time updates)
  // This ensures Status Overview updates immediately when WebSocket events are received
  const prevTasksLengthRef = useRef<number>(0);
  const prevTasksStatusHashRef = useRef<string>('');

  useEffect(() => {
    // Calculate a hash of task statuses to detect status changes
    const statusHash = tasks.map(t => `${t.id}:${t.status}`).join('|');
    const tasksChanged =
      tasks.length !== prevTasksLengthRef.current ||
      statusHash !== prevTasksStatusHashRef.current;

    if (tasksChanged && tasks.length > 0) {
      const newStats = calculateStats(tasks);
      setStats(newStats);

      // Update refs
      prevTasksLengthRef.current = tasks.length;
      prevTasksStatusHashRef.current = statusHash;
    }
  }, [tasks, calculateStats]);

  // Phase 4: 自动刷新定时器
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // 清除之前的定时器
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    // 如果启用自动刷新，设置新的定时器
    if (isAutoRefreshEnabled) {
      refreshTimerRef.current = setInterval(async () => {
        await fetchTasks();
        await fetchStats();
        setLastRefreshTime(new Date());
      }, refreshInterval);
    }

    // 清理函数
    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, [isAutoRefreshEnabled, refreshInterval, fetchTasks, fetchStats]);

  // Phase 5+: Auto-fallback to polling when WebSocket is not connected
  // This ensures the page still updates even if WebSocket connection fails
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const FALLBACK_POLLING_INTERVAL = 15000; // 15 seconds fallback polling

  // Track previous connection state to detect reconnection
  const wasConnectedRef = useRef<boolean | null>(null);

  useEffect(() => {
    // Clear existing polling timer
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    // If realtime is enabled but not connected, start fallback polling
    // Skip if manual auto-refresh is already enabled
    if (enableRealtime && !isRealtimeConnected && !isAutoRefreshEnabled) {
      pollingIntervalRef.current = setInterval(async () => {
        await fetchTasks();
        await fetchStats();
        setLastRefreshTime(new Date());
      }, FALLBACK_POLLING_INTERVAL);
    } else if (isRealtimeConnected) {
      // Phase 5+: Refetch on reconnect to catch any missed updates
      // Only refetch if transitioning from disconnected → connected (not on initial connect)
      if (wasConnectedRef.current === false) {
        fetchTasks();
        fetchStats();
        setLastRefreshTime(new Date());
      }
    }

    // Update previous connection state (skip initial null state)
    if (wasConnectedRef.current !== null || isRealtimeConnected !== undefined) {
      wasConnectedRef.current = isRealtimeConnected;
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [enableRealtime, isRealtimeConnected, isAutoRefreshEnabled, fetchTasks, fetchStats]);

  // Phase 5+: Refetch when page becomes visible (handles tab switching)
  // This ensures data is fresh after user returns from Order page
  const lastVisibilityRefetchRef = useRef<number>(0);
  const MIN_VISIBILITY_REFETCH_INTERVAL = 5000; // Don't refetch more than once per 5 seconds

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        const timeSinceLastRefetch = now - lastVisibilityRefetchRef.current;

        // Only refetch if enough time has passed since last refetch
        if (timeSinceLastRefetch > MIN_VISIBILITY_REFETCH_INTERVAL) {
          // Tab became visible, refetch tasks
          lastVisibilityRefetchRef.current = now;
          fetchTasks();
          fetchStats();
          setLastRefreshTime(new Date());
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchTasks, fetchStats]);

  return {
    // Data
    tasks,
    drivers,
    driversData,
    shippers,
    destinations,
    ports,
    stats,
    // State
    loading,
    error,
    // Phase 4: 自动刷新状态
    isAutoRefreshEnabled,
    lastRefreshTime,
    // Phase 5: 实时连接状态
    isRealtimeConnected,
    lastRealtimeUpdate,
    // Actions
    startFixPlate,
    startGateFee,
    startGateFeeBatch,
    fetchTasks,
    fetchStats,  // Phase 2: 单独获取统计
    updateTask,
    updateTaskStatus,
    assignDriver,
    refetch,
    // Phase 4: 自动刷新控制
    toggleAutoRefresh,
    setRefreshInterval,
    // Phase 4: 批量操作
    batchUpdateStatus,
    batchAssignDriver,
    batchDeleteTasks,
    // Pagination
    total,
    limit,
    offset
  };
};

